import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, HStack, Text, Collapse, Button, Icon, Stack } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { FullPageSpinner } from "components/Spinner";
import { useSessionCtx } from "utils";
import { AddPaymentMethodButton } from "components/AddPaymentMethod";
import useGetPaymentMethods from "./hooks/useGetPaymentMethods";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import PaymentMethod from "./PaymentMethod";
import Wallet from "./Wallet";
import PaymentMethodList from "./PaymentMethodsList";
import { useSettings } from "utils/settingsContext";
import { getIfAmountCanBeCoveredByPMs, getIfAmountIsWithinLimitsForPM } from "./utils";

export { PaymentMethod };
export { Wallet };
export { PaymentMethodList };

const getUsedWallets = (wallets, amount) => {
  // sort wallets by largest to smallest
  wallets.sort((a, b) => b.provider_data.balance - a.provider_data.balance);
  let remainingAmount = amount;
  let usedWallets = [];

  // handle '0' amount case, by assigning a wallet just so there's a pm used in the invoice and exit
  if (amount === 0 && wallets.length) {
    return {
      usedWallets: [{ ...wallets[0], amountUsed: 0 }],
      remainingAmount: 0,
    };
  }

  for (const wallet of wallets) {
    if (remainingAmount <= 0) {
      // covered the amount and can stop
      break;
    }

    // current wallet has enough to cover the amount
    if (wallet.provider_data.balance >= remainingAmount) {
      usedWallets.push({
        ...wallet,
        amountUsed: remainingAmount,
      });
      remainingAmount = 0;
    }
    // subtract all from the wallet and proceed to the next wallet
    else {
      usedWallets.push({ ...wallet, amountUsed: wallet.provider_data.balance });
      remainingAmount -= wallet.provider_data.balance;
    }
  }
  return { usedWallets, remainingAmount };
};

/**
 * @name isNewSelectedPaymentMethodAnyDifferentThanPrevious
 * @description Check differences between 2 'selectedPaymentMethods' payloads
 */
const isNewSelectedPaymentMethodAnyDifferentThanPrevious = (newSelection, prevSelection) => {
  const newUsedWallets = newSelection?.usedWallets || [];
  const prevUsedWallets = prevSelection?.usedWallets || [];
  const newUsedPM = newSelection?.usedPaymentMethod?.payment_method_id;
  const prevUsedPM = prevSelection?.usedPaymentMethod?.payment_method_id;
  const samePMID = newUsedPM === prevUsedPM;
  const sameWalletInfo =
    newUsedWallets.length === prevUsedWallets.length &&
    newUsedWallets.every((w, i) => {
      const prevWallet = prevUsedWallets[i];
      return (
        w.payment_method_id === prevWallet?.payment_method_id &&
        w.amountUsed === prevWallet?.amountUsed
      );
    });
  return samePMID && sameWalletInfo;
};

/**
 * PaymentMethodSelector
 * TODO: this is a major candidate for refactoring...
 * @description
 * Used for selecting payment methods.
 * This component renders the entire list of payment methods
 * and lets user pick just enough payment methods to complete their
 * transaction.
 * @param {object} props
 * @param {object} props.chooserConfig - Required. "filter" used to get the correct payment methods.
 * @param {Number} props.amount - The amount that is being transacted.
 * @param {Array} props.params - Required. The params for the get transaction methods API.
 * @param {Array} props.paymentMethodsUsedAmount - Payment methods that are used, with the used amount.
 * @param {Function} props.onChange - Callback when a payment method is selected.
 * @param {Function} props.onSubmit - Callback when user clicks submit.
 * @param {Function} props.onError - Callback when there's an error.
 * @param {Boolean} [props.disableSubmitButton] - Optional.  Disables submit button (there is some internal logic around disabling as well called 'submitDisabledInternally' which is tied on firing onChange, need to separate these to not have issues with firing onChange)
 * @param {React.ReactNode} [props.consentSlot] - Optional.  Rendered directly above the submit
 * button.  Exists because the submit button is this component's last child, so a consent block
 * placed after <PaymentMethodSelector> by the caller would sit *below* the CTA it gates - a
 * compliance-visibility problem, not just a layout one - and placing it before would put it above
 * the whole payment method list.  Callers that pass nothing are unaffected.
 */
const PaymentMethodSelector = ({
  chooserConfig,
  amount,
  params,
  onChange,
  onSubmit,
  onError,
  enableAutoRedirect,
  onAddPMOpen,
  onLoading,
  paymentMethodsUsedAmount = [],
  disableSubmitButton = false,
  pmidErrorList = [],
  preferredPaymentMethodId = null,
  consentSlot = null,
}) => {
  const preferredPaymentMethodIdRef = useRef(preferredPaymentMethodId);
  const { sessionCtx } = useSessionCtx();
  const { integratorSettings } = useSettings();
  const { hideUserWallets, selectButton } = sessionCtx?.flowConfig || {};
  const [isReady, setIsReady] = useState(false);
  const [isPaymentMethodSelectorOpen, setIsPaymentMethodSelectorOpen] = useState(false);
  const [isPMSelectionRequired, setIsPMSelectionRequired] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState({
    usedWallets: [],
    usedPaymentMethod: null,
  });
  const [restPaymentMethods, setRestPaymentMethods] = useState([]);
  const previousSelectedPaymentMethods = useRef(selectedPaymentMethods);
  const getPaymentMethods = useGetPaymentMethods({
    chooserConfig,
    params,
  });
  const previousAmount = useRef(amount); // if the amount changes during the flow (i.e. invoice amount differes from authorized invoice amount as a result of taxes, etc) then we want to use this to check for valid PM options/selections
  const previousPmidErrorList = useRef(pmidErrorList); // we'll want to look for changes in this value so we can expand payment options

  const { paymentMethods, isUpdated, isPending: isLoading, refetch, error } = getPaymentMethods;
  /**
   * processNewPaymentSelection
   * @description when we update selected payment methods, we also want to
   * update the list of pms that are not in the selection (restPaymentMethods).
   *
   * This will merge in the 'pmSelectionOverrides' into whatever
   * current value is in 'selectedPaymentMethods' by using the function
   * argument to the useState setter so we don't have to add
   * 'selectedPaymentMethods' to what the useCallback is monitoring.
   *
   * Since this function gets called within a useEffect, and
   * we don't want to retrigger the useEffect when 'paymentMethods'
   * gets updated, we take paymentMethods as an argument so we don't
   * have to monitor it for changes.
   */
  const processNewPaymentSelection = useCallback(
    (pmSelectionOverrides, paymentMethods) => {
      setSelectedPaymentMethods((currVal) => {
        const newVal = {
          ...currVal,
          ...pmSelectionOverrides,
        };
        const restPaymentMethods = paymentMethods.filter(
          (pm) => pm.provider !== "wallet" && pm.id !== newVal.usedPaymentMethod?.id,
        );
        setRestPaymentMethods(restPaymentMethods);
        return newVal;
      });
    },
    [setSelectedPaymentMethods, setRestPaymentMethods],
  );

  const showPaymentOptionsWhenPmidErrorListUpdated = useCallback(() => {
    if (pmidErrorList.length !== previousPmidErrorList.current.length) {
      previousPmidErrorList.current = pmidErrorList; // update previous value to current
      setIsPaymentMethodSelectorOpen(true);
    }
  }, [pmidErrorList, previousPmidErrorList, setIsPaymentMethodSelectorOpen]);

  // useEffect to handle expanding payment options when a new pmid error comes in
  useEffect(() => {
    showPaymentOptionsWhenPmidErrorListUpdated();
  }, [showPaymentOptionsWhenPmidErrorListUpdated]);

  const getNonWalletRemainingBalanceOrFullAmountFromSelectedPMs = useCallback(
    (selectedPMs, amountToCheck) => {
      const amountInWallets = (selectedPMs?.usedWallets || []).reduce(
        (acc, w) => acc + w.provider_data.balance,
        0,
      );
      return (amountToCheck || 0) - amountInWallets <= 0
        ? amountToCheck
        : (amountToCheck || 0) - amountInWallets;
    },
    [],
  );

  /**
   * @name processDefaultSelectionAndUpdatedAmounts
   * @description
   * This function has 2 purposes:
   * - Make a default payment method selection when we have enough info to make that selection
   * - Handle when an update occurs (either by updated paymentMethods or amount prop changes). In
   *   this case we check if there is an existing PM selection and if so see if it still is valid
   *   for the new information we have, if so keep it, if not set a new default.
   */
  const processDefaultSelectionAndUpdatedAmounts = useCallback(
    ({ paymentMethods, amount, selectedPaymentMethods, pmidErrorList }) => {
      const purchaseTransactionLimits = integratorSettings?.transaction_limits?.purchase || {};

      // If we have a preferred PM (e.g. newly added card), attempt to select it.
      // This check runs on every evaluation so that if the PM list was stale on
      // the first pass (missing the new card), we pick it up once it appears.
      if (preferredPaymentMethodIdRef.current) {
        const wallets = hideUserWallets
          ? []
          : paymentMethods.filter((pm) => pm.provider === "wallet" && pm.provider_data.balance);

        const preferredPM = paymentMethods.find(
          (pm) => pm.id === preferredPaymentMethodIdRef.current,
        );
        const preferredPMWithinLimits = preferredPM
          ? getIfAmountIsWithinLimitsForPM({
              pm: preferredPM,
              txnLimits: purchaseTransactionLimits,
              amountToCheck: amount,
            }).amountIsWithinLimits
          : false;
        const shouldUsePreferredPM =
          !!preferredPM &&
          preferredPM.provider !== "wallet" &&
          !pmidErrorList.includes(preferredPM.payment_method_id) &&
          preferredPMWithinLimits;

        if (shouldUsePreferredPM) {
          preferredPaymentMethodIdRef.current = null; // one-time use
          processNewPaymentSelection(
            {
              usedWallets: [],
              usedPaymentMethod: preferredPM,
            },
            paymentMethods,
          );
          setIsReady(true);
          setWallets(wallets);
          setIsPMSelectionRequired(false);
          return;
        }
        // If the preferred PM wasn't found yet (stale list), fall through to
        // normal default selection so the UI isn't stuck with nothing selected.
      }

      // check if we already have payment method selections (this could previously be set
      // by this function as a default, or could be set by user selection)
      let noActivePMSelections =
        !selectedPaymentMethods.usedPaymentMethod &&
        selectedPaymentMethods.usedWallets.length === 0;

      if (!noActivePMSelections) {
        // we have existing selected payment methods, let's make sure they still have enough to cover the amount
        const amountInWallets = selectedPaymentMethods.usedWallets.reduce(
          (acc, w) => acc + w.provider_data.balance,
          0,
        );

        const amountIsNotProvided = !amount && amount !== 0;
        const unableToUseWallets = amountIsNotProvided || amountInWallets < (amount || 0); // if there is no 'amount' we need to check against, or we dont have enough to cover the 'amount' being requested
        const selectedPMNotFound = !(
          selectedPaymentMethods.usedPaymentMethod &&
          paymentMethods.find((pm) => pm.id === selectedPaymentMethods.usedPaymentMethod.id)
        );

        const pmHasIssues =
          selectedPaymentMethods.usedPaymentMethod?.id &&
          pmidErrorList.includes(selectedPaymentMethods.usedPaymentMethod.id);
        const noUsableUsedPaymentMethod = selectedPMNotFound || pmHasIssues;
        if (unableToUseWallets && noUsableUsedPaymentMethod) {
          // if current selections no longer work, then let's just treat it as no selections having been made yet, so we'll handle the defaults
          noActivePMSelections = true;
        }
      }

      if (noActivePMSelections) {
        // get wallets
        const wallets = hideUserWallets
          ? []
          : paymentMethods.filter((pm) => pm.provider === "wallet" && pm.provider_data.balance);
        // check if there are no usable PMs
        const amountCanBeCoveredByPMs = getIfAmountCanBeCoveredByPMs(
          paymentMethods,
          amount,
          purchaseTransactionLimits,
          hideUserWallets,
        );
        if (!amountCanBeCoveredByPMs && enableAutoRedirect) {
          // enableAutoRedirect just exists to make sure we only do this once for the user and don't put them back in a loop.  See wrapping logic to this component.
          onAddPMOpen();
        }

        // "usedWallets" will contain wallets we can use by default, then remainingAmount will need to be handled by a non-wallet PM
        const { usedWallets, remainingAmount } = getUsedWallets(wallets, amount);
        const isPMSelectionRequired = remainingAmount > 0 || !wallets.length;

        let defaultPaymentMethod = isPMSelectionRequired
          ? paymentMethods.find((pm) => {
              const { amountIsWithinLimits } = getIfAmountIsWithinLimitsForPM({
                pm,
                txnLimits: purchaseTransactionLimits,
                amountToCheck: remainingAmount,
              });
              return pm.isDefault && amountIsWithinLimits;
            }) ||
            (paymentMethods.length &&
              paymentMethods.find((pm) => {
                const { amountIsWithinLimits } = getIfAmountIsWithinLimitsForPM({
                  pm,
                  txnLimits: purchaseTransactionLimits,
                  amountToCheck: remainingAmount,
                });
                return pm.provider !== "wallet" && amountIsWithinLimits;
              }))
          : null;

        // check if there was a previous issue with this PM
        if (
          defaultPaymentMethod &&
          pmidErrorList.includes(defaultPaymentMethod.payment_method_id)
        ) {
          defaultPaymentMethod = null;
        }

        processNewPaymentSelection(
          {
            usedWallets,
            usedPaymentMethod: isPMSelectionRequired ? defaultPaymentMethod : null,
          },
          paymentMethods,
        );

        setIsReady(true); // this really only matters the first time
        setWallets(wallets);
        setIsPMSelectionRequired(isPMSelectionRequired);
      } else {
        // we have previously selected payment methods here that can cover the balance.
        // It's possible we may have more than we need though (only in the case of wallets+PM).
        // So here, just handling the case where we have a wallet + another payment method,
        // but we now have an amount where just the wallet is enough, so let's remove the
        // unneeded PM.
        let paymentSelectionOverrides = {}; // by default let's not override any current selections, but be ready to update payment methods in case there was a change there
        if (
          selectedPaymentMethods.usedWallets.length > 0 &&
          selectedPaymentMethods.usedPaymentMethod
        ) {
          const amountInWallets = selectedPaymentMethods.usedWallets.reduce(
            (acc, w) => acc + w.provider_data?.balance || 0,
            0,
          );
          if (amountInWallets >= amount) {
            paymentSelectionOverrides = { usedPaymentMethod: null }; // remove the additional PM selection
          }
        }
        processNewPaymentSelection(paymentSelectionOverrides, paymentMethods); // making sure we call this here since even if we're not chaning selected PMs we want to make sure updated PMs get rendered
      }
    },
    [
      hideUserWallets,
      enableAutoRedirect,
      onAddPMOpen,
      processNewPaymentSelection,
      integratorSettings,
    ],
  );

  useEffect(() => {
    /**
     * useEffect to handle setting a default payment
     * selection when we have our payment methods available.
     * Also handles cases where the amount changes (this
     * can happen when taxes or fees change based on the
     * new payment method selections)
     */
    const isNewAmount = previousAmount.current !== amount;
    const selectedPMID = selectedPaymentMethods.usedPaymentMethod?.payment_method_id;
    const selectedPMHasIssue = selectedPMID && pmidErrorList.includes(selectedPMID);
    if (isUpdated || isNewAmount || selectedPMHasIssue) {
      // only do something if we have updated PMs to choose from, or a new amount to handle
      previousAmount.current = amount;
      processDefaultSelectionAndUpdatedAmounts({
        paymentMethods,
        amount,
        selectedPaymentMethods,
        pmidErrorList,
      });
    }
  }, [
    isUpdated,
    paymentMethods,
    amount,
    selectedPaymentMethods,
    processDefaultSelectionAndUpdatedAmounts,
    pmidErrorList,
  ]);

  useEffect(() => {
    if (error) {
      onError && onError(error);
    }
  }, [error, onError]);

  useEffect(() => {
    onLoading && onLoading(isLoading || !isReady);
  }, [onLoading, isLoading, isReady]);

  const getPaymentMethodAmount = (usedPaymentMethod) => {
    if (usedPaymentMethod) {
      const foundPM = paymentMethodsUsedAmount.find(
        (pm) => pm.payment_method_id === usedPaymentMethod.id,
      );
      if (foundPM) {
        return {
          authorized_amount: foundPM.authorized_amount,
          currency: foundPM.currency,
          display_amount: foundPM.display_amount,
        };
      }
      return {};
    }
    return {};
  };

  // upon selection, see if we need to toggle it off
  // if we need to toggle it off, then a PM selection is required
  // if it's being toggled on, then calculate how much the wallets used
  // and if an additional PM selection is required.
  const handleWalletSelect = () => {
    if (selectedPaymentMethods.usedWallets.length) {
      processNewPaymentSelection({ usedWallets: [] }, paymentMethods);
      setIsPMSelectionRequired(true);
    } else {
      const { usedWallets, remainingAmount } = getUsedWallets(wallets, amount);
      const isPMSelectionRequired = remainingAmount > 0;
      processNewPaymentSelection(
        {
          usedWallets: usedWallets,
          usedPaymentMethod: isPMSelectionRequired
            ? selectedPaymentMethods.usedPaymentMethod
            : null,
        },
        paymentMethods,
      );
      setIsPMSelectionRequired(isPMSelectionRequired);
    }

    if (isPaymentMethodSelectorOpen) {
      // collapse section after PM selection since this can make the 'buy' button difficult to find
      setIsPaymentMethodSelectorOpen(false);
    }
  };

  // un-select the PM if its the same PM being selected
  // else set the PM
  const handlePaymentMethodSelect = (paymentMethod) => {
    if (selectedPaymentMethods.usedPaymentMethod?.id === paymentMethod.id) {
      // setSelectedPaymentMethods((curState) => ({ ...curState, usedPaymentMethod: null }));
    } else {
      // if PM selection is required, then perserve the used wallet state
      if (isPMSelectionRequired) {
        processNewPaymentSelection({ usedPaymentMethod: paymentMethod }, paymentMethods);
      }
      // else only 1 PM is allowed, so un-select all used wallets
      else {
        processNewPaymentSelection(
          {
            usedWallets: [],
            usedPaymentMethod: paymentMethod,
          },
          paymentMethods,
        );
      }
    }

    if (isPaymentMethodSelectorOpen) {
      // collapse section after PM selection since this can make the 'buy' button difficult to find
      setIsPaymentMethodSelectorOpen(false);
    }
  };

  const toggleShowPaymentMethod = () => {
    setIsPaymentMethodSelectorOpen((curValue) => !curValue);
  };

  const handleSubmit = () => {
    onSubmit && onSubmit(selectedPaymentMethods);
  };

  const submitDisabledInternally =
    (isPMSelectionRequired && !selectedPaymentMethods.usedPaymentMethod) ||
    (!isPMSelectionRequired &&
      !selectedPaymentMethods.usedPaymentMethod &&
      !selectedPaymentMethods.usedWallets.length);

  useEffect(() => {
    const isSamePMs = isNewSelectedPaymentMethodAnyDifferentThanPrevious(
      selectedPaymentMethods,
      previousSelectedPaymentMethods.current,
    );
    previousSelectedPaymentMethods.current = selectedPaymentMethods;
    if (onChange && !isSamePMs) {
      onChange({ ...selectedPaymentMethods, isValid: !submitDisabledInternally });
    }
  }, [selectedPaymentMethods, submitDisabledInternally, onChange]);

  if (isLoading || !isReady) {
    return <FullPageSpinner data-testid="payment-method-selector" />;
  }

  return (
    <Box mt="3">
      <Stack spacing="4" data-testid="primary-display-payment-selection">
        {wallets && wallets.length ? (
          <Wallet
            wallets={wallets}
            isSelected={selectedPaymentMethods.usedWallets.length}
            onSelect={handleWalletSelect}
          />
        ) : null}
        {selectedPaymentMethods.usedPaymentMethod ? (
          <PaymentMethod
            paymentMethod={{
              ...selectedPaymentMethods.usedPaymentMethod,
              ...getPaymentMethodAmount(selectedPaymentMethods.usedPaymentMethod),
            }}
            isSelected={selectedPaymentMethods.usedPaymentMethod}
            onUpdate={getPaymentMethods.refetch}
            onSelect={handlePaymentMethodSelect}
          />
        ) : null}
      </Stack>
      {restPaymentMethods.length > 0 && (
        <HStack
          spacing="1"
          my="5"
          color="integrator.400"
          cursor="pointer"
          onClick={toggleShowPaymentMethod}
        >
          <Text>
            <FormattedMessage defaultMessage="Other payment methods" description="label" />
          </Text>
          <Icon as={isPaymentMethodSelectorOpen ? BiChevronUp : BiChevronDown} w="6" h="6" />
        </HStack>
      )}
      {restPaymentMethods.length === 0 && !isPaymentMethodSelectorOpen && (
        <Box mt="4">
          <AddPaymentMethodButton
            dataTestId="add-payment-method-outside-collapse"
            onOpen={onAddPMOpen}
          />
        </Box>
      )}
      <Collapse in={isPaymentMethodSelectorOpen}>
        <PaymentMethodList
          paymentMethods={restPaymentMethods}
          onUpdate={refetch}
          selectedPaymentMethodId={selectedPaymentMethods.usedPaymentMethod?.id}
          onSelect={handlePaymentMethodSelect}
          pmidErrorList={pmidErrorList}
          transactionLimits={integratorSettings?.transaction_limits?.purchase || {}}
          amountToCheck={getNonWalletRemainingBalanceOrFullAmountFromSelectedPMs(
            selectedPaymentMethods,
            amount,
          )}
        />
        <AddPaymentMethodButton
          dataTestId="add-payment-method-inside-collapse"
          onOpen={onAddPMOpen}
        />
      </Collapse>

      {consentSlot && (
        // The button's top margin moves onto this wrapper so consent sits directly above the
        // button rather than a gap away from it.
        <Box mt="50px">{consentSlot}</Box>
      )}

      <Button
        data-testid="payment-method-selector-submit-button"
        // No-slot callers keep the original 100px untouched
        mt={consentSlot ? 0 : "100px"}
        w="100%"
        type="submit"
        isDisabled={submitDisabledInternally || disableSubmitButton}
        onClick={handleSubmit}
      >
        {selectButton?.label ? (
          selectButton.label
        ) : (
          <FormattedMessage
            defaultMessage="Use this payment method"
            description="Submit button label"
          />
        )}
      </Button>
    </Box>
  );
};

export default PaymentMethodSelector;
